// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    image: ImageModel;
    useLargeImage?: boolean;
    onClick?: () => void;
    extendedStyles?: ProductPrimaryImageStyles;
    badgePostion?: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export interface ProductPrimaryImageStyles {
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;
}

export const productPrimaryImageStyles: ProductPrimaryImageStyles = {
    centeringWrapper: {
        css: css`
            min-height: 150px;
            flex-grow: 1;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            img {
                height: auto;
                max-width: 100%;
            }
        `,
    },
    hiddenWrapper: {
        css: css`
            opacity: 0;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    image: {
        css: css`
            cursor: pointer;
        `,
    },
};

const getBadgePosition = (pos?: string) => {
    switch (pos) {
        case "topRight":
            return `top: 0; right: 0;`;
        case "topLeft":
            return `top: 0; left: 0;`;
        case "bottomRight":
            return `bottom: 0; right: 0;`;
        case "bottomLeft":
            return `bottom: 0; left: 0;`;
        default:
            return `top: 0; right: 0;`;
    }
};

const ProductPrimaryImage = ({
    productSettings,
    product,
    image,
    useLargeImage,
    onClick,
    extendedStyles,
    badgePostion,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productPrimaryImageStyles, extendedStyles));

    const [lastHeight, setLastHeight] = useState(0);
    const [lastInstance, setLastInstance] = useState<HTMLElement | null>(null);
    const setRefHandler = (instance: HTMLElement | null) => {
        if (instance) {
            setLastInstance(instance);
        }
    };

    const [isLoading, setIsLoading] = useState(false);
    const onLoadHandler = useCallback(() => {
        setIsLoading(false);
        setTimeout(() => {
            if (lastInstance && lastInstance.clientHeight > 0) {
                setLastHeight(lastInstance.clientHeight);
            }
        }, 100);
    }, [setIsLoading, lastInstance, setLastHeight]);

    useEffect(() => {
        if (image?.imageType === "Static") {
            setIsLoading(true);
        }
    }, [image?.id]);

    if (!product) {
        return null;
    }

    useEffect(() => {
        if ((window as any).sirvScriptAdded) {
            return;
        }

        if (
            productSettings.imageProvider !== "SIRV" ||
            !product.images ||
            product.images.every(o => o.imageType !== "360")
        ) {
            return;
        }

        const script = document.createElement("script");
        script.src = "https://scripts.sirv.com/sirv.js";
        script.async = true;

        document.body.appendChild(script);
        (window as any).sirvScriptAdded = true;
    }, [productSettings.imageProvider, product]);

    const imageWrapperClickHandler = () => {
        onClick?.();
    };

    const path = useLargeImage
        ? image.largeImagePath || image.mediumImagePath
        : image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }

    const showBadge = product.properties?.badgeIndicator ? JSON.parse(product.properties?.badgeIndicator).Icon : "";

    const imageWrapperKeyHandler: React.KeyboardEventHandler<HTMLImageElement> = event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Enter") {
            onClick?.();
        }
    };

    return (
        <LoadingOverlay
            loading={isLoading}
            css={css`
                height: ${lastHeight}px;
                ${isLoading ? `max-height: 366px;` : ""}
            `}
        >
            {image.imageType === "Static" && (
                <StyledWrapper
                    css={css`
                        position: relative;
                        flex-grow: 1;
                        display: flex;
                        justfiy-content: center;
                        align-items: center;
                        &&:hover {
                            cursor: pointer;
                        }
                    `}
                    ref={setRefHandler}
                    onClick={imageWrapperClickHandler}
                >
                    <LazyImage
                        {...styles.image}
                        key={image.id}
                        src={path}
                        cloudinaryWidth={styles?.image?.cloudinaryWidth}
                        cloudinary={styles?.image?.cloudinary}
                        altText={image.imageAltText}
                        onLoad={onLoadHandler}
                        onError={onLoadHandler}
                        data-test-selector="productDetails_mainImage"
                        onKeyPress={imageWrapperKeyHandler}
                        tabIndex={0}
                    />
                    {showBadge && badgePostion && (
                        <StyledWrapper
                            css={css`
                                position: absolute;
                                background: rgba(255, 255, 255, 0.7);
                                ${getBadgePosition(badgePostion)}
                                max-width: 65px;
                                max-height: 65px;
                                img {
                                    opacity: 0.7;
                                }
                            `}
                        >
                            <LazyImage src={showBadge} cloudinary={true} cloudinaryWidth="60" />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )}
            {image.imageType === "360" && productSettings.imageProvider === "SIRV" && (
                <div style={{ minHeight: lastHeight }} onClick={imageWrapperClickHandler}>
                    <div className="Sirv" key={image.id} data-src={image.mediumImagePath} />
                </div>
            )}
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps)(ProductPrimaryImage);
